import { useT } from '@/store';
import { useDriverTelegramQr } from '@/hooks/useDriverAdmin';
import { Modal } from '@/components/ui/Overlay';
import { ErrorState, SkeletonRows } from '@/components/ui/Panel';

/**
 * Driver onboarding: a QR the driver scans to link their Telegram account,
 * after which they can receive dispatch messages and sign into the Mini App
 * without a password.
 *
 * The SVG comes from the backend rather than a client-side QR library — it
 * already generates one for this exact purpose, and the deep link it encodes
 * (`?start=DRV_<driver_id>`) is the backend's format to own, not ours to
 * reconstruct.
 *
 * A 400 here is the normal fresh-install state, not a fault: it means
 * TELEGRAM_BOT_USERNAME is unset, so no link can exist yet. It is reported as
 * a configuration message rather than an error, and deliberately kept out of
 * the driver list so it cannot break that whole view.
 */
export function TelegramQrModal({ driver, onClose }) {
  const t = useT();
  const { data, isLoading, isError, error } = useDriverTelegramQr(driver?.driver_id);

  return (
    <Modal open={Boolean(driver)} onClose={onClose} title={`${t('drivers.telegramQr')} · ${driver?.full_name ?? ''}`}>
      {isLoading ? <SkeletonRows rows={4} /> : null}

      {isError ? (
        <ErrorState>
          {error?.status === 400 ? t('drivers.qrNeedsBotUsername') : error?.message}
        </ErrorState>
      ) : null}

      {data ? (
        <div className="space-y-3 text-center">
          {/* The SVG is generated by our own backend from the driver id — not
              user input — so injecting it is safe here. */}
          <div
            className="mx-auto inline-block rounded-[12px] bg-white p-3 [&>svg]:block [&>svg]:h-[200px] [&>svg]:w-[200px]"
            dangerouslySetInnerHTML={{ __html: data.svg }}
          />
          <p className="break-all text-[11px] text-dim">{data.url}</p>
          <a href={data.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary inline-block">
            {t('drivers.openLink')}
          </a>
          <p className="text-[11px] leading-snug text-dim">{t('drivers.qrHint')}</p>
        </div>
      ) : null}
    </Modal>
  );
}
